// PetPack.jsx — Panel del canal PetPack (comida y esenciales de mascotas)
// dentro del Admin de Limpack. Solo superadmin. Aislado: opera el schema
// `petpack` vía RPCs petpack_admin_* (ver lib/petpackApi.js). ABM de productos,
// ajuste de stock con ledger, y palancas del canal (config).
import { useEffect, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { useCanEdit } from '../../lib/useCanEdit'
import {
  petpackGetProductos, petpackGetCategorias, petpackGetConfig, petpackGetMovimientos,
  petpackUpsertProducto, petpackToggle, petpackBorrarProducto, petpackRestaurarProducto,
  petpackAjustarStock, petpackUpdateConfig, petpackDisponible,
} from '../../lib/petpackApi'

const PET = '#2FA84D'  // verde PetPack

const money = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')
function precioKg(p) {
  const kg = Number(p?.peso_kg || 0)
  if (!kg) return null
  return Math.round(Number(p?.precio_publico || 0) / kg)
}

export default function PetPack() {
  const canEdit = useCanEdit()
  const [productos, setProductos] = useState([])
  const [categorias, setCategorias] = useState([])
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtro, setFiltro] = useState('todos')  // todos | publicados | despublicados | borrados
  const [q, setQ] = useState('')
  const [modal, setModal] = useState(null)        // { producto } | null
  const [stockModal, setStockModal] = useState(null)
  const [showConfig, setShowConfig] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true); setError(null)
    try {
      const [prods, cats, cfg] = await Promise.all([
        petpackGetProductos(true), petpackGetCategorias(), petpackGetConfig(),
      ])
      setProductos(prods || []); setCategorias(cats || []); setConfig(cfg || null)
    } catch (e) {
      setError(e.message || String(e))
    } finally { setLoading(false) }
  }

  const catMap = Object.fromEntries((categorias || []).map(c => [c.id, c.nombre]))

  const filtrados = (productos || []).filter(p => {
    if (filtro === 'borrados') return !!p.deleted_at
    if (p.deleted_at) return false
    if (filtro === 'publicados') return p.publicado
    if (filtro === 'despublicados') return !p.publicado
    return true
  }).filter(p => {
    if (!q.trim()) return true
    const s = (p.nombre + ' ' + (p.marca || '') + ' ' + (p.sku || '')).toLowerCase()
    return s.includes(q.toLowerCase())
  })

  const vivos = (productos || []).filter(p => !p.deleted_at)
  const nPublicados = vivos.filter(p => p.publicado).length
  const nBajos = vivos.filter(p => petpackDisponible(p) <= Number(p.stock_minimo || 0)).length

  async function togglePublicado(p) {
    if (!canEdit) return
    try { await petpackToggle(p.id, 'publicado', !p.publicado); await load() }
    catch (e) { alert('No se pudo: ' + (e.message || e)) }
  }

  return (
    <AdminLayout title="PetPack">
      <div className="page">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <img src="/petpack-logo.png" alt="PetPack" style={{ height: 44, width: 'auto', borderRadius: 8, display: 'block' }} />
          <h1 style={{ margin: 0 }}>Pet<span style={{ color: PET }}>Pack</span></h1>
          <span style={{ background: '#E9F7EC', color: PET, fontWeight: 700, fontSize: 11, padding: '4px 10px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Canal mascotas
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={() => setShowConfig(true)}>⚙️ Config del canal</button>
        </div>
        <p className="lead">Catálogo y stock del canal de comida de mascotas. Precios y costos CON IVA.</p>

        {error && <div className="err-box" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card"><h3>Productos</h3><div className="stat">{vivos.length}</div></div>
          <div className="card"><h3>Publicados</h3><div className="stat" style={{ color: PET }}>{nPublicados}</div></div>
          <div className="card"><h3>Stock bajo</h3><div className="stat" style={{ color: nBajos ? '#B45309' : 'inherit' }}>{nBajos}</div><div className="desc">Disponible ≤ mínimo</div></div>
          <div className="card"><h3>Canal</h3><div className="stat" style={{ color: config?.activo ? PET : '#991B1B', fontSize: 18 }}>{config?.activo ? '● Activo' : 'Apagado'}</div></div>
        </div>

        <div className="btn-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          {canEdit && <button className="btn primary" style={{ background: PET, borderColor: PET }} onClick={() => setModal({ producto: null })}>+ Nuevo producto</button>}
          <button className="btn ghost" onClick={load}>↺</button>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre, marca o SKU…"
            style={{ padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 8, minWidth: 240 }} />
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', gap: 4, background: '#F1F5F9', borderRadius: 999, padding: 4 }}>
            {[
              { id: 'todos', label: 'Todos' },
              { id: 'publicados', label: 'Publicados' },
              { id: 'despublicados', label: 'Ocultos' },
              { id: 'borrados', label: 'Borrados' },
            ].map(f => (
              <button key={f.id} className="btn ghost" onClick={() => setFiltro(f.id)}
                style={{ padding: '6px 14px', fontSize: 12, borderRadius: 999,
                  background: filtro === f.id ? '#fff' : 'transparent',
                  border: filtro === f.id ? '1px solid var(--line)' : '1px solid transparent',
                  fontWeight: filtro === f.id ? 700 : 500 }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {!canEdit && (
          <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', color: '#78350F', padding: '8px 12px', borderRadius: 8, fontSize: 12, margin: '10px 0' }}>
            👁️ Estás como <strong>lectura</strong>. Solo superadmin puede crear, editar o mover stock de PetPack.
          </div>
        )}

        {loading ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>Cargando…</div>
        ) : filtrados.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>
            No hay productos {filtro !== 'todos' ? `(${filtro})` : ''}. {canEdit && filtro !== 'borrados' && 'Cargá el primero con "+ Nuevo producto".'}
          </div>
        ) : (
          <table className="t-table">
            <thead>
              <tr>
                <th>Producto</th><th>Categoría</th>
                <th style={{ textAlign: 'right' }}>Precio</th>
                <th style={{ textAlign: 'right' }}>Stock</th>
                <th>Publicado</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(p => {
                const disp = petpackDisponible(p)
                const bajo = disp <= Number(p.stock_minimo || 0)
                const pk = precioKg(p)
                return (
                  <tr key={p.id} style={{ opacity: p.deleted_at ? 0.5 : 1 }}>
                    <td>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        {p.imagen_url
                          ? <img src={p.imagen_url} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 6, background: '#F8FAFC' }} />
                          : <div style={{ width: 40, height: 40, borderRadius: 6, background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🦴</div>}
                        <div>
                          <div style={{ fontWeight: 700 }}>{p.nombre}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                            {[p.marca, p.presentacion, p.sku].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{catMap[p.categoria_id] || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 800 }}>{money(p.precio_publico)}</div>
                      {pk && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{money(pk)}/kg</div>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 800, color: bajo ? '#B45309' : 'inherit' }}>{disp}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {Number(p.stock_actual || 0)} − {Number(p.stock_reservado || 0)} res.
                      </div>
                    </td>
                    <td>
                      <button onClick={() => togglePublicado(p)} disabled={!canEdit || !!p.deleted_at}
                        title={canEdit ? 'Tocar para cambiar' : 'Solo superadmin'}
                        style={{ cursor: canEdit && !p.deleted_at ? 'pointer' : 'default', border: 'none', background: 'transparent', padding: 0 }}>
                        <span style={{
                          background: p.publicado ? '#D1FAE5' : '#F1F5F9', color: p.publicado ? '#065F46' : '#64748B',
                          padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: '.06em' }}>
                          {p.publicado ? '● Publicado' : 'Oculto'}
                        </span>
                      </button>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {p.deleted_at ? (
                        canEdit && <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 11 }}
                          onClick={async () => { await petpackRestaurarProducto(p.id); load() }}>Restaurar</button>
                      ) : (
                        <>
                          <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setModal({ producto: p })}>Editar</button>
                          {canEdit && <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => setStockModal(p)}>Stock</button>}
                          {canEdit && <button className="btn ghost" style={{ padding: '4px 10px', fontSize: 11, color: '#B91C1C' }}
                            onClick={async () => { if (confirm(`¿Borrar "${p.nombre}"? (se puede restaurar)`)) { await petpackBorrarProducto(p.id); load() } }}>Borrar</button>}
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {modal && <ProductoModal producto={modal.producto} categorias={categorias} onClose={() => setModal(null)} onDone={() => { setModal(null); load() }} />}
        {stockModal && <StockModal producto={stockModal} onClose={() => setStockModal(null)} onDone={() => { setStockModal(null); load() }} />}
        {showConfig && <ConfigModal config={config} onClose={() => setShowConfig(false)} onDone={(c) => { setConfig(c); setShowConfig(false) }} />}
      </div>
    </AdminLayout>
  )
}

function ProductoModal({ producto, categorias, onClose, onDone }) {
  const esNuevo = !producto
  const [f, setF] = useState({
    id: producto?.id || '',
    sku: producto?.sku || '', nombre: producto?.nombre || '', marca: producto?.marca || '',
    presentacion: producto?.presentacion || '', especie: producto?.especie || 'perro',
    categoria_id: producto?.categoria_id || '', etapa: producto?.etapa || '', tamano_raza: producto?.tamano_raza || '',
    peso_kg: producto?.peso_kg ?? '', costo_unitario: producto?.costo_unitario ?? '', precio_publico: producto?.precio_publico ?? '',
    iva_pct: producto?.iva_pct ?? 22, precio_programado: producto?.precio_programado ?? '',
    stock_minimo: producto?.stock_minimo ?? 0, stock_critico: producto?.stock_critico ?? 0,
    imagen_url: producto?.imagen_url || '', descripcion: producto?.descripcion || '',
    publicado: producto?.publicado ?? false, activo: producto?.activo ?? true, destacado: producto?.destacado ?? false,
    peso_g: producto?.peso_g ?? '', largo_cm: producto?.largo_cm ?? '', ancho_cm: producto?.ancho_cm ?? '', alto_cm: producto?.alto_cm ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const set = (k, v) => setF(s => ({ ...s, [k]: v }))

  const margen = (() => {
    const pv = Number(f.precio_publico || 0), cu = Number(f.costo_unitario || 0)
    if (!pv) return null
    return Math.round(((pv - cu) / pv) * 100)
  })()

  async function submit(e) {
    e.preventDefault(); setErr(null)
    if (!f.sku.trim()) { setErr('Falta SKU.'); return }
    if (!f.nombre.trim()) { setErr('Falta nombre.'); return }
    setSaving(true)
    try {
      await petpackUpsertProducto({ ...f, id: f.id || undefined })
      onDone()
    } catch (e) {
      setErr(e.message?.includes('duplicate') || e.message?.includes('unique') ? 'Ya existe un producto con ese SKU.' : (e.message || String(e)))
    } finally { setSaving(false) }
  }

  return (
    <Overlay onClose={onClose}>
      <h2 style={{ marginBottom: 4 }}>{esNuevo ? 'Nuevo producto' : `Editar ${producto.nombre}`}</h2>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 0 }}>Precios y costo CON IVA. El stock se ajusta desde el botón "Stock".</p>
      <form onSubmit={submit}>
        <Row>
          <Field label="SKU" required><input value={f.sku} onChange={e => set('sku', e.target.value)} placeholder="LP-EUK-15" /></Field>
          <Field label="Nombre" required><input value={f.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Eukanuba Adulto" /></Field>
        </Row>
        <Row>
          <Field label="Marca"><input value={f.marca} onChange={e => set('marca', e.target.value)} /></Field>
          <Field label="Presentación"><input value={f.presentacion} onChange={e => set('presentacion', e.target.value)} placeholder="15 kg" /></Field>
        </Row>
        <Row>
          <Field label="Especie">
            <select value={f.especie} onChange={e => set('especie', e.target.value)}>
              <option value="perro">Perro</option><option value="gato">Gato</option>
            </select>
          </Field>
          <Field label="Categoría">
            <select value={f.categoria_id || ''} onChange={e => set('categoria_id', e.target.value)}>
              <option value="">— sin categoría —</option>
              {(categorias || []).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Etapa"><input value={f.etapa} onChange={e => set('etapa', e.target.value)} placeholder="adulto / cachorro / senior" /></Field>
          <Field label="Tamaño de raza"><input value={f.tamano_raza} onChange={e => set('tamano_raza', e.target.value)} placeholder="pequeña / mediana / grande" /></Field>
        </Row>
        <Row>
          <Field label="Peso neto (kg)" hint="para el precio/kg"><input type="number" step="0.001" value={f.peso_kg} onChange={e => set('peso_kg', e.target.value)} /></Field>
          <Field label="IVA %"><input type="number" value={f.iva_pct} onChange={e => set('iva_pct', e.target.value)} /></Field>
        </Row>
        <Row>
          <Field label="Costo unitario (c/IVA)"><input type="number" value={f.costo_unitario} onChange={e => set('costo_unitario', e.target.value)} /></Field>
          <Field label="Precio público (c/IVA)" hint={margen !== null ? `margen ${margen}%` : ''}><input type="number" value={f.precio_publico} onChange={e => set('precio_publico', e.target.value)} /></Field>
        </Row>
        <Row>
          <Field label="Precio programado (opcional)" hint="si vacío, se aplica el % del canal"><input type="number" value={f.precio_programado} onChange={e => set('precio_programado', e.target.value)} /></Field>
          <Field label="Imagen (URL)"><input value={f.imagen_url} onChange={e => set('imagen_url', e.target.value)} placeholder="https://…" /></Field>
        </Row>
        <Row>
          <Field label="Stock mínimo"><input type="number" value={f.stock_minimo} onChange={e => set('stock_minimo', e.target.value)} /></Field>
          <Field label="Stock crítico"><input type="number" value={f.stock_critico} onChange={e => set('stock_critico', e.target.value)} /></Field>
        </Row>
        <Field label="Descripción"><textarea rows="2" value={f.descripcion} onChange={e => set('descripcion', e.target.value)} /></Field>
        <details style={{ margin: '4px 0 12px' }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--muted)' }}>Dimensiones de envío (opcional)</summary>
          <Row>
            <Field label="Peso (g)"><input type="number" value={f.peso_g} onChange={e => set('peso_g', e.target.value)} /></Field>
            <Field label="Largo (cm)"><input type="number" value={f.largo_cm} onChange={e => set('largo_cm', e.target.value)} /></Field>
          </Row>
          <Row>
            <Field label="Ancho (cm)"><input type="number" value={f.ancho_cm} onChange={e => set('ancho_cm', e.target.value)} /></Field>
            <Field label="Alto (cm)"><input type="number" value={f.alto_cm} onChange={e => set('alto_cm', e.target.value)} /></Field>
          </Row>
        </details>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 10 }}>
          <Check label="Publicado (visible en la tienda)" checked={f.publicado} onChange={v => set('publicado', v)} />
          <Check label="Activo" checked={f.activo} onChange={v => set('activo', v)} />
          <Check label="Destacado" checked={f.destacado} onChange={v => set('destacado', v)} />
        </div>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn primary" style={{ background: PET, borderColor: PET }} disabled={saving}>
            {saving ? 'Guardando…' : (esNuevo ? 'Crear producto' : 'Guardar')}
          </button>
        </div>
      </form>
    </Overlay>
  )
}

function StockModal({ producto, onClose, onDone }) {
  const [nuevo, setNuevo] = useState(String(producto.stock_actual ?? 0))
  const [motivo, setMotivo] = useState('')
  const [movs, setMovs] = useState([])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => { petpackGetMovimientos(producto.id, 30).then(setMovs).catch(() => {}) }, [producto.id])

  async function guardar(e) {
    e.preventDefault(); setErr(null); setSaving(true)
    try { await petpackAjustarStock(producto.id, Number(nuevo), motivo.trim() || null); onDone() }
    catch (e) { setErr(e.message || String(e)); setSaving(false) }
  }

  return (
    <Overlay onClose={onClose}>
      <h2 style={{ marginBottom: 4 }}>Stock · {producto.nombre}</h2>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 0 }}>
        Actual: <strong>{Number(producto.stock_actual || 0)}</strong> · reservado: {Number(producto.stock_reservado || 0)} · disponible: {petpackDisponible(producto)}.
        El reservado lo mueven los pedidos, no se toca a mano.
      </p>
      <form onSubmit={guardar}>
        <Row>
          <Field label="Nuevo stock actual"><input type="number" step="0.01" value={nuevo} onChange={e => setNuevo(e.target.value)} autoFocus /></Field>
          <Field label="Motivo"><input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="compra, conteo, rotura…" /></Field>
        </Row>
        {err && <div className="err-box">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cerrar</button>
          <button type="submit" className="btn primary" style={{ background: PET, borderColor: PET }} disabled={saving}>{saving ? 'Guardando…' : 'Ajustar stock'}</button>
        </div>
      </form>
      <h3 style={{ marginTop: 20, fontSize: 13 }}>Últimos movimientos</h3>
      {movs.length === 0 ? <p style={{ fontSize: 12, color: 'var(--muted)' }}>Sin movimientos.</p> : (
        <table className="t-table" style={{ fontSize: 12 }}>
          <thead><tr><th>Fecha</th><th>Tipo</th><th style={{ textAlign: 'right' }}>Cant.</th><th style={{ textAlign: 'right' }}>Después</th><th>Motivo</th></tr></thead>
          <tbody>
            {movs.map(m => (
              <tr key={m.id}>
                <td>{new Date(m.created_at).toLocaleDateString('es-UY')}</td>
                <td>{m.tipo}</td>
                <td style={{ textAlign: 'right' }}>{Number(m.cantidad)}</td>
                <td style={{ textAlign: 'right' }}>{m.stock_despues != null ? Number(m.stock_despues) : '—'}</td>
                <td style={{ color: 'var(--muted)' }}>{m.motivo || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Overlay>
  )
}

function ConfigModal({ config, onClose, onDone }) {
  const [f, setF] = useState({
    envio_gratis_desde: config?.envio_gratis_desde ?? 1500,
    descuento_programado_pct: config?.descuento_programado_pct ?? 10,
    frecuencia_default_dias: config?.frecuencia_default_dias ?? 30,
    costo_envio_estandar: config?.costo_envio_estandar ?? 0,
    ahorro_min_pct: config?.ahorro_min_pct ?? 12,
    aviso_dias_antes: config?.aviso_dias_antes ?? 2,
    activo: config?.activo ?? true,
    wa_template_pago: config?.wa_template_pago ?? '',
    wa_template_lang: config?.wa_template_lang ?? 'es_AR',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const set = (k, v) => setF(s => ({ ...s, [k]: v }))

  async function guardar(e) {
    e.preventDefault(); setErr(null); setSaving(true)
    try { const c = await petpackUpdateConfig(f); onDone(c) }
    catch (e) { setErr(e.message || String(e)); setSaving(false) }
  }

  return (
    <Overlay onClose={onClose}>
      <h2 style={{ marginBottom: 4 }}>Config del canal PetPack</h2>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 0 }}>Palancas del canal. Todo con IVA.</p>
      <form onSubmit={guardar}>
        <Row>
          <Field label="Envío gratis desde ($)"><input type="number" value={f.envio_gratis_desde} onChange={e => set('envio_gratis_desde', e.target.value)} /></Field>
          <Field label="Costo de envío ($)"><input type="number" value={f.costo_envio_estandar} onChange={e => set('costo_envio_estandar', e.target.value)} /></Field>
        </Row>
        <Row>
          <Field label="Descuento programado (%)"><input type="number" value={f.descuento_programado_pct} onChange={e => set('descuento_programado_pct', e.target.value)} /></Field>
          <Field label="Ahorro mínimo comunicado (%)"><input type="number" value={f.ahorro_min_pct} onChange={e => set('ahorro_min_pct', e.target.value)} /></Field>
        </Row>
        <Row>
          <Field label="Frecuencia default (días)"><input type="number" value={f.frecuencia_default_dias} onChange={e => set('frecuencia_default_dias', e.target.value)} /></Field>
          <Field label="Aviso antes del envío (días)"><input type="number" value={f.aviso_dias_antes} onChange={e => set('aviso_dias_antes', e.target.value)} /></Field>
        </Row>
        <Row>
          <Field label="Plantilla WhatsApp (código de pago)" hint="nombre aprobado en Meta"><input value={f.wa_template_pago} onChange={e => set('wa_template_pago', e.target.value)} placeholder="petpack_codigo_pago" /></Field>
          <Field label="Idioma plantilla"><input value={f.wa_template_lang} onChange={e => set('wa_template_lang', e.target.value)} placeholder="es_AR" /></Field>
        </Row>
        <Check label="Canal activo (si lo apagás, el scheduler no genera ni avisa)" checked={f.activo} onChange={v => set('activo', v)} />
        {err && <div className="err-box" style={{ marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn primary" style={{ background: PET, borderColor: PET }} disabled={saving}>{saving ? 'Guardando…' : 'Guardar config'}</button>
        </div>
      </form>
    </Overlay>
  )
}

// ---------- helpers UI ----------
function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,31,61,.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 640, width: '100%', margin: '24px 0' }}>
        {children}
      </div>
    </div>
  )
}
function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>
}
function Field({ label, children, required, hint }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <b style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--navy-ink, #1A3461)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>
        {label} {required && <span style={{ color: '#DC2626' }}>*</span>}
        {hint && <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--muted)', marginLeft: 6 }}>{hint}</span>}
      </b>
      {children}
    </label>
  )
}
function Check({ label, checked, onChange }) {
  return (
    <label style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ width: 'auto' }} />
      {label}
    </label>
  )
}
